import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";

import {
  ApiError,
  apiClient,
  type ReplenishmentPolicy,
  type ReplenishmentPolicyDraft,
  type ReplenishmentRecommendation,
  type ReplenishmentRun,
} from "../api/client";
import { useAuth } from "../app/AuthContext";

export function ReplenishmentPage() {
  const { session } = useAuth();
  const [policies, setPolicies] = useState<ReplenishmentPolicy[]>([]);
  const [selectedPolicyId, setSelectedPolicyId] = useState("");
  const [datasetIds, setDatasetIds] = useState("");
  const [destinationId, setDestinationId] = useState("");
  const [recommendations, setRecommendations] = useState<ReplenishmentRecommendation[]>([]);
  const [run, setRun] = useState<ReplenishmentRun | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const canManage = session?.role === "manager" || session?.role === "owner";
  const selectedPolicy = policies.find((item) => item.id === selectedPolicyId) ?? policies[0];

  const loadPolicies = useCallback(async () => {
    const values = await apiClient.listReplenishmentPolicies();
    setPolicies(values);
    setSelectedPolicyId((current) =>
      values.some((item) => item.id === current) ? current : (values[0]?.id ?? ""),
    );
  }, []);

  const loadRecommendations = useCallback(async (scopeId?: string) => {
    const values = await apiClient.listReplenishmentRecommendations(scopeId);
    setRecommendations(values);
  }, []);

  useEffect(() => {
    Promise.all([loadPolicies(), loadRecommendations()]).catch((error: unknown) => {
      setMessage(errorMessage(error));
    });
  }, [loadPolicies, loadRecommendations]);

  async function start(event: FormEvent) {
    event.preventDefault();
    if (!selectedPolicy) return;
    const ids = datasetIds
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
    if (!ids.length || !destinationId.trim()) {
      setMessage("请填写至少一个数据集版本 ID 和目的仓 ID。");
      return;
    }
    setBusy(true);
    setMessage("");
    try {
      const created = await apiClient.startReplenishment({
        scope_id: selectedPolicy.scope_id,
        policy_code: selectedPolicy.code,
        dataset_version_ids: ids,
        destination_id: destinationId.trim(),
      });
      setRun(created);
      setMessage("补货 Workflow 已进入队列；完成后刷新建议列表即可复核。");
    } catch (error) {
      setMessage(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  async function review(
    item: ReplenishmentRecommendation,
    decision: "accepted" | "rejected",
  ) {
    if (!item.id) return;
    setBusy(true);
    try {
      const updated = await apiClient.reviewReplenishmentRecommendation(item.id, decision);
      setRecommendations((current) =>
        current.map((candidate) => (candidate.id === updated.id ? updated : candidate)),
      );
    } catch (error) {
      setMessage(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page-stack replenishment-page">
      <header className="flow-header">
        <div>
          <span className="eyebrow">固定 Workflow · 仅建议</span>
          <h1>供应链补货建议</h1>
          <p>使用已发布策略计算断货、下单时间与数量；接受建议不会创建订单或 ERP 交易。</p>
        </div>
        <span className="phase-badge">{policies.length} 个可用策略</span>
      </header>

      <section className="flow-panel" aria-labelledby="replenishment-run-title">
        <div className="panel-heading">
          <div>
            <span className="eyebrow">输入与策略快照</span>
            <h2 id="replenishment-run-title">创建补货运行</h2>
          </div>
          {run && <span className="phase-badge">{run.state}</span>}
        </div>
        <form className="replenishment-form" onSubmit={start}>
          <label>
            已发布策略
            <select
              onChange={(event) => setSelectedPolicyId(event.target.value)}
              value={selectedPolicy?.id ?? ""}
            >
              {!policies.length && <option value="">暂无已发布策略</option>}
              {policies.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.code}@{item.version} · {item.scope_id}
                </option>
              ))}
            </select>
          </label>
          <label>
            数据集版本 ID（多个用逗号分隔）
            <input onChange={(event) => setDatasetIds(event.target.value)} value={datasetIds} />
          </label>
          <label>
            目的仓 ID
            <input
              onChange={(event) => setDestinationId(event.target.value)}
              value={destinationId}
            />
          </label>
          <button disabled={busy || !selectedPolicy} type="submit">
            {busy ? "正在提交…" : "启动固定 Workflow"}
          </button>
        </form>
        {run && <p className="status-banner">运行 {run.id} · {run.execution_mode} · {run.state}</p>}
        {message && <p className="status-banner" role="status">{message}</p>}
      </section>

      <section aria-labelledby="replenishment-results-title">
        <div className="section-heading">
          <div>
            <span className="eyebrow">可追溯建议</span>
            <h2 id="replenishment-results-title">员工复核</h2>
          </div>
          <button
            className="secondary-button"
            onClick={() => void loadRecommendations(selectedPolicy?.scope_id)}
            type="button"
          >
            刷新建议
          </button>
        </div>
        <div className="recommendation-grid">
          {recommendations.map((item) => (
            <RecommendationCard
              canReview={canManage}
              item={item}
              key={item.id ?? `${item.destination_id}-${item.calculation_version}`}
              onReview={review}
            />
          ))}
          {!recommendations.length && <div className="empty-page">暂无可复核补货建议。</div>}
        </div>
      </section>

      {canManage && <PolicyManager onPublished={loadPolicies} policies={policies} />}
    </div>
  );
}

function RecommendationCard({
  item,
  canReview,
  onReview,
}: {
  item: ReplenishmentRecommendation;
  canReview: boolean;
  onReview: (
    item: ReplenishmentRecommendation,
    decision: "accepted" | "rejected",
  ) => Promise<void>;
}) {
  const insufficient = item.status === "insufficient_data";
  return (
    <article className={`recommendation-card ${insufficient ? "insufficient" : ""}`}>
      <div className="finding-heading">
        <div>
          <span className="task-type">{insufficient ? "数据不足" : `${item.risk_level ?? "未知"} 风险`}</span>
          <h3>{item.recommended_quantity ?? "—"} {item.quantity_unit ?? ""}</h3>
        </div>
        <strong>{item.review_decision ?? "pending"}</strong>
      </div>
      {insufficient ? (
        <p>缺少或不可用：{item.missing_inputs.join("、")}</p>
      ) : (
        <dl className="recommendation-details">
          <div><dt>预计断货</dt><dd>{item.projected_stockout_at ?? "不适用"}</dd></div>
          <div><dt>建议下单</dt><dd>{item.recommended_order_at ?? "不适用"}</dd></div>
          <div><dt>物流</dt><dd>{String(item.logistics_recommendation?.method ?? "—")}</dd></div>
          <div><dt>计算版本</dt><dd>{item.calculation_version}</dd></div>
        </dl>
      )}
      {!!item.warnings.length && <p className="warning-copy">{item.warnings.join("；")}</p>}
      <details>
        <summary>查看中间值与来源</summary>
        <pre>{JSON.stringify({ intermediate_values: item.intermediate_values, source_references: item.source_references }, null, 2)}</pre>
      </details>
      <div className="review-actions">
        {item.artifact_id && (
          <a className="secondary-button" href={apiClient.replenishmentArtifactUrl(item.artifact_id)}>
            下载制品
          </a>
        )}
        {canReview && item.id && (
          <>
            <button onClick={() => void onReview(item, "accepted")} type="button">接受</button>
            <button className="danger-button" onClick={() => void onReview(item, "rejected")} type="button">拒绝</button>
          </>
        )}
      </div>
    </article>
  );
}

function PolicyManager({
  policies,
  onPublished,
}: {
  policies: ReplenishmentPolicy[];
  onPublished: () => Promise<void>;
}) {
  const [scopeId, setScopeId] = useState(policies[0]?.scope_id ?? "");
  const [code, setCode] = useState(policies[0]?.code ?? "us-default");
  const [targetDays, setTargetDays] = useState(60);
  const [message, setMessage] = useState("");
  const latest = useMemo(
    () => policies.filter((item) => item.code === code && item.scope_id === scopeId).sort((a, b) => b.version - a.version)[0],
    [code, policies, scopeId],
  );

  useEffect(() => {
    if (!scopeId && policies[0]) setScopeId(policies[0].scope_id);
    if (code === "us-default" && policies[0]) setCode(policies[0].code);
  }, [code, policies, scopeId]);

  async function save(event: FormEvent) {
    event.preventDefault();
    const draft = policyDraft(code, scopeId, targetDays);
    try {
      const created = latest
        ? await apiClient.createReplenishmentPolicyVersion(latest.id, draft)
        : await apiClient.createReplenishmentPolicy(draft);
      await apiClient.publishReplenishmentPolicy(created.id);
      setMessage(`已发布 ${created.code}@${created.version}`);
      await onPublished();
    } catch (error) {
      setMessage(errorMessage(error));
    }
  }

  return (
    <section className="flow-panel" aria-labelledby="policy-manager-title">
      <span className="eyebrow">manager / owner</span>
      <h2 id="policy-manager-title">补货策略管理</h2>
      <p className="panel-copy">编辑已发布策略会自动创建下一版草稿，旧运行继续引用原版本。</p>
      <form className="replenishment-form" onSubmit={save}>
        <label>店铺范围 ID<input onChange={(event) => setScopeId(event.target.value)} value={scopeId} /></label>
        <label>策略代码<input onChange={(event) => setCode(event.target.value)} value={code} /></label>
        <label>目标覆盖天数<input min="1" onChange={(event) => setTargetDays(Number(event.target.value))} type="number" value={targetDays} /></label>
        <button disabled={!scopeId || !code || targetDays < 1} type="submit">{latest ? "创建新版并发布" : "创建并发布"}</button>
      </form>
      {message && <p className="status-banner" role="status">{message}</p>}
    </section>
  );
}

function policyDraft(code: string, scopeId: string, targetDays: number): ReplenishmentPolicyDraft {
  return {
    code: code.trim(),
    scope_id: scopeId.trim(),
    formula: { engine: "deterministic-v1" },
    thresholds: {
      target_cover_days: targetDays,
      reorder_review_days: 7,
      air_freight_threshold_days: 14,
      expedited_sea_threshold_days: 35,
    },
    units: { quantity: "unit", velocity: "unit/day", lead_time: "day" },
    safety_stock: { default_days: 14 },
    promotion_factors: { default: "1" },
  };
}

function errorMessage(error: unknown): string {
  return error instanceof ApiError ? error.message : "补货操作失败，请稍后重试。";
}
